import { OnboardingSavePublicTransportDto, DayOfWeek } from '@interfaces/models';
import { ActiveServicePreview, ServiceContainer } from '@shared/components';
import { CityDto, ZoneDto } from '@interfaces/types';
import { Tickets } from '@ui/components/icons';
import { useTranslation } from 'react-i18next';
import { Separator } from '@ui/components';
import { useEffect, FC } from 'react';

import { PublicTransportConfigurationForm } from './public-transport-configuration-form/public-transport-configuration-form';
import { usePublicTransportConfiguration } from './use-public-transport-configuration';

interface PublicTransportConfigurationProps {
  onConfigurationChange: (config: OnboardingSavePublicTransportDto | null) => void;
  setIsEditing?: (isEditing: boolean) => void;
  initialConfiguration?: OnboardingSavePublicTransportDto | null;
  defaultSelected?: boolean;
  toggleDisabled?: boolean;
  canNotEdit?: boolean;
  className?: string;
  days: DayOfWeek[];
  cities: CityDto[];
  zones: ZoneDto[];
}

export const PublicTransportConfiguration: FC<PublicTransportConfigurationProps> = ({
  onConfigurationChange,
  initialConfiguration,
  defaultSelected,
  toggleDisabled,
  setIsEditing,
  canNotEdit,
  className,
  cities,
  zones,
  days,
}) => {
  const { t } = useTranslation();

  const {
    preparePublicTransportPreviewData,
    createDefaultConfiguration,
    handleRevertChanges,
    handleActiveChange,
    isEntityActive,
    configuration,
    handleEdit,
    handleSave,
    isEditing,
  } = usePublicTransportConfiguration({
    onUpdate: onConfigurationChange,
    initialConfiguration,
    defaultSelected,
    cities,
    zones,
    days,
  });

  useEffect(() => setIsEditing?.(isEditing), [isEditing, setIsEditing]);

  const defaultPublicTransportConfiguration = createDefaultConfiguration();
  const publicTransportConfigurationPreviewData = preparePublicTransportPreviewData(
    configuration ?? defaultPublicTransportConfiguration,
  );

  return (
    <ServiceContainer
      title={t('COMPANY_POLICY.PUBLIC_TRANSPORT_CONFIGURATION_TITLE')}
      previewData={publicTransportConfigurationPreviewData}
      onActiveChange={handleActiveChange}
      toggleDisabled={toggleDisabled}
      active={isEntityActive}
      className={className}
      icon={<Tickets />}
    >
      <>
        {(isEditing || isEntityActive) && <Separator className="mt-4 mb-4" />}

        <div>
          {isEntityActive && !isEditing && (
            <ActiveServicePreview
              data={publicTransportConfigurationPreviewData}
              canNotEdit={canNotEdit}
              onEdit={handleEdit}
            />
          )}

          {isEditing && (
            <div>
              <PublicTransportConfigurationForm
                defaultPublicTransportConfiguration={
                  configuration ?? defaultPublicTransportConfiguration
                }
                onRevertChanges={handleRevertChanges}
                onSubmit={handleSave}
                cities={cities}
                zones={zones}
                days={days}
              />
            </div>
          )}
        </div>
      </>
    </ServiceContainer>
  );
};
